import { getFluidPropertiesContext } from "@/libs/calculations/fluidProperties/getFluidPropertiesContext";
import { extrapolateMDByTVD } from "@/libs/calculations/deviationSurvey";
import { resolveCasingInteriorDiameterAtDepth } from "@/libs/calculations/casingFrictionLoss";
import {
  buildIntervalCasingGeometry,
  calculatePerfsAndDatumPressure
} from "../../casingFrictionCalculations";
import type { FluidPropertiesPipelineInputs } from "../types";

// Fluid properties at datum, P_wf at perfs, and the pressure gradient used
// to get well pressure at any TVD.
export const calculateDatumAndPressureGradient = (
  inputs: FluidPropertiesPipelineInputs
) => {
  const {
    basicData,
    correlations,
    desiredFlowRates,
    wellFlowingPressureAtDatumPSIA,
    datumPointTVD,
    bubblePointPressurePSIA,
    condensateApi,
    dewPointPressurePSIA,
    entryRows,
    customFluidProperties,
    customViscosityProperties,
    fluidPropertiesOverrides,
    bottomOfEquipmentMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  } = inputs;
  let perfsAndDatumCasingFrictionPSIA = 0;

  const casingInteriorDiameterAtPerfsIn = resolveCasingInteriorDiameterAtDepth(
    basicData.topOfPerforationsMDFt,
    topCasingInteriorDiameterIn,
    topCasingLengthFt,
    bottomCasingInteriorDiameterIn,
    bottomCasingLengthFt
  );
  const casingInteriorDiameterAtBottomOfEquipmentRawIn =
    resolveCasingInteriorDiameterAtDepth(
      bottomOfEquipmentMDFt,
      topCasingInteriorDiameterIn,
      topCasingLengthFt,
      bottomCasingInteriorDiameterIn,
      bottomCasingLengthFt
    );

  // Datum can be anywhere downhole where the user has well test data for
  // Now uses desiredFlowRates since if it is in PI mode, our fluid properties
  // are way too low using PI rates to find our casing friction loss pressure
  const fluidPropertiesAtDatumContext = getFluidPropertiesContext(
    basicData,
    correlations,
    wellFlowingPressureAtDatumPSIA,
    basicData.bottomholeTemperatureF,
    bottomCasingInteriorDiameterIn,
    desiredFlowRates,
    undefined,
    undefined,
    customFluidProperties,
    customViscosityProperties,
    bubblePointPressurePSIA,
    dewPointPressurePSIA,
    0,
    0,
    fluidPropertiesOverrides,
    condensateApi
  );

  // P_wf (perfs)
  const perfsAndDatumPressureProperties = calculatePerfsAndDatumPressure({
    wellFlowingPressureAtDatumPSIA,
    correlations,
    datumPointFt: basicData.datumPointFt,
    datumPointTVD,
    topOfPerforationsMDFt: basicData.topOfPerforationsMDFt,
    topOfPerforationsTVDFt: basicData.topOfPerforationsTVDFt,
    entryRows,
    fluidPropertiesAtDatumContext,
    surfaceTemperatureF: basicData.surfaceTemperatureF,
    bottomholeTemperatureF: basicData.bottomholeTemperatureF,
    casingGeometry: buildIntervalCasingGeometry(
      basicData.datumPointFt,
      basicData.topOfPerforationsMDFt,
      topCasingInteriorDiameterIn,
      topCasingLengthFt,
      bottomCasingInteriorDiameterIn,
      bottomCasingLengthFt
    ),
    bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
    producingBottomholePressurePSIA: basicData.producingBottomholePressurePSIA
  });

  // Set well pressure at perfs
  const wellFlowingPressureAtPerfsPSIA =
    perfsAndDatumPressureProperties.endingPressurePSIA;
  perfsAndDatumCasingFrictionPSIA =
    perfsAndDatumPressureProperties.casingFrictionProperties
      .frictionPressurePSIA;
  let wellFlowingPressureAtReferenceForGradientPSIA =
    perfsAndDatumPressureProperties.pressureForGradientPSIA!;

  // Perfs can be our reference point unless it is within 1 ft of datum.
  // Need to be different since this reference point is finding our pressure
  // gradient for the well flowing pressure.
  let referencePointTVDFt = basicData.topOfPerforationsTVDFt;
  let referencePointMDFt = basicData.topOfPerforationsMDFt;
  if (Math.abs(datumPointTVD - basicData.topOfPerforationsTVDFt) <= 1) {
    // Extract TVD range from deviation survey data (TVD is at index 3)
    // If no survey data, fall back to casing-based range
    const tvdValues =
      entryRows.data.length > 0
        ? entryRows.data.map(row => row[3])
        : [0, topCasingLengthFt + bottomCasingLengthFt];

    const minTVD = Math.min(...tvdValues);
    const maxTVD = Math.max(...tvdValues);

    // Calculate ideal reference point: try datum ± 500 ft, clamped to survey range
    let candidateReferencePointTVD = datumPointTVD + 500;

    // If candidate is outside range, try the opposite offset
    // Try to subtract smaller and smaller numbers then
    if (candidateReferencePointTVD > maxTVD) {
      let candidateOffsetFt = 500;
      let downwardCandidateTVD = datumPointTVD - candidateOffsetFt;

      while (
        (downwardCandidateTVD <= minTVD ||
          downwardCandidateTVD > maxTVD ||
          downwardCandidateTVD === datumPointTVD) &&
        candidateOffsetFt > Number.EPSILON
      ) {
        candidateOffsetFt /= 2;
        downwardCandidateTVD = datumPointTVD - candidateOffsetFt;
      }

      candidateReferencePointTVD = downwardCandidateTVD;
    }

    referencePointTVDFt = candidateReferencePointTVD;
    referencePointMDFt = extrapolateMDByTVD(referencePointTVDFt, entryRows);
    const datumAndReferencePointPressureProperties =
      calculatePerfsAndDatumPressure({
        wellFlowingPressureAtDatumPSIA,
        correlations,
        datumPointFt: basicData.datumPointFt,
        datumPointTVD,
        topOfPerforationsMDFt: referencePointMDFt,
        topOfPerforationsTVDFt: referencePointTVDFt,
        entryRows,
        fluidPropertiesAtDatumContext,
        surfaceTemperatureF: basicData.surfaceTemperatureF,
        bottomholeTemperatureF: basicData.bottomholeTemperatureF,
        casingGeometry: buildIntervalCasingGeometry(
          basicData.datumPointFt,
          referencePointMDFt,
          topCasingInteriorDiameterIn,
          topCasingLengthFt,
          bottomCasingInteriorDiameterIn,
          bottomCasingLengthFt
        ),
        bottomCasingPressurePSIA: basicData.bottomCasingPressurePSIA,
        producingBottomholePressurePSIA:
          basicData.producingBottomholePressurePSIA
      });
    wellFlowingPressureAtReferenceForGradientPSIA =
      datumAndReferencePointPressureProperties.pressureForGradientPSIA!;
  }

  // Compute gradient to use at different tvd depths
  const gradientReferenceDeltaTVDFt = referencePointTVDFt - datumPointTVD;
  const hasUsableGradientReference =
    Number.isFinite(wellFlowingPressureAtReferenceForGradientPSIA) &&
    Number.isFinite(gradientReferenceDeltaTVDFt) &&
    Math.abs(gradientReferenceDeltaTVDFt) > 1;

  let pressureGradientPSIPerFt = 0;
  if (hasUsableGradientReference) {
    pressureGradientPSIPerFt =
      (wellFlowingPressureAtReferenceForGradientPSIA -
        wellFlowingPressureAtDatumPSIA) /
      gradientReferenceDeltaTVDFt;
  } else {
    // Fall back to perfs-based gradient to avoid collapsing downstream pressures
    // when datum/reference TVD separation is effectively zero.
    const perfsDeltaTVDFt = basicData.topOfPerforationsTVDFt - datumPointTVD;
    if (Math.abs(perfsDeltaTVDFt) > 1) {
      pressureGradientPSIPerFt =
        (wellFlowingPressureAtPerfsPSIA - wellFlowingPressureAtDatumPSIA) /
        perfsDeltaTVDFt;
    }
  }

  if (!Number.isFinite(pressureGradientPSIPerFt)) {
    pressureGradientPSIPerFt = 0;
  }

  // Nested helper function
  // This is what getWellPressureAtDepth used to do
  const getPressureAtReferenceTVDPSIA = (referenceTVDFt: number): number =>
    (referenceTVDFt - datumPointTVD) * pressureGradientPSIPerFt +
    wellFlowingPressureAtDatumPSIA;

  return {
    casingInteriorDiameterAtPerfsIn,
    casingInteriorDiameterAtBottomOfEquipmentRawIn,
    fluidPropertiesAtDatumContext,
    wellFlowingPressureAtPerfsPSIA,
    perfsAndDatumCasingFrictionPSIA,
    getPressureAtReferenceTVDPSIA
  };
};
